// «طلبات الموقع» عند البياع (0113) — قرار المالك (22/09/2026): ما يملؤه العميل على
// icontravel.net يصل البياع هنا **بالدور بين الأونلاين**، مع جرسٍ وصوت — «وكمان يجي على
// الإيميل عادي».
//
// **كل طلب يقول إنه من الموقع**: شارةٌ «من الموقع» واسم النموذج (اتصل بنا / طيران) — فلا
// يُخلط بعميلٍ من بنك الأرقام أو من الصفحة. وما كتبه العميل يُعرض كما وصل: الرقم كما
// كتبه لو لم يُفهم، والرسالة، وبقيّة الحقول (تواريخ السفر، عدد الأفراد…).
//
// **و«سجّل متابعة» تملأ النموذج** بالاسم والرقم والرسالة — فلا يكتبهم مرّتين. ولمّا تُسجَّل
// المتابعة بهذا الرقم يُقفل الطلب بها ويخرج من المفتوح إلى «اتكلمت».
//
// **ويُعاد التحميل كل دقيقة** كالجرس: الطلب الذي وصل وأنت على الشاشة يظهر بلا تحديث يدوي.
import { useCallback, useEffect, useState } from 'react';
import { ApiError, get, post, type WebLead, type WebLeadsResponse } from '../lib/api.ts';
import { Alert, Badge, Card, Empty, Spinner } from '../components/ui.tsx';
import { PhoneActions } from '../components/PhoneActions.tsx';
import { TrashIcon } from '../components/TrashIcon.tsx';
import { cairoDateAr } from '../lib/format.ts';
import { usePulse } from '../lib/pulse.tsx';
import type { LeadPrefill } from './LeadBankCard.tsx';

/** حقول النموذج التي عُرضت في مكانها المخصّص — لا تُكرَّر في قائمة «باقي الحقول» */
const SHOWN_KEYS = new Set(['الاسم', 'اسمك', 'رقم الهاتف', 'البريد الإلكتروني', 'الرسالة', 'name', 'phone', 'email', 'message']);

/**
 * باقي الحقول — **بلا ما ورد في الرسالة نفسها** (قرار المالك 23/09/2026: «الغى ظهور الرسايل مكرره»):
 * طلبُ الشات القديم من الموقع كان يكتب «من → إلى: … · موعد السفر: …» في الرسالة **وفي** الحقول معًا،
 * فتظهر البيانات مرّتين في البطاقة.
 */
export function extraFields(l: WebLead): [string, string][] {
  const msg = l.message ?? '';
  return Object.entries(l.fields ?? {})
    .filter(([k, v]) => !SHOWN_KEYS.has(k) && v.trim() !== '' && !msg.includes(`${k}: ${v.trim()}`));
}

/** ما يُملأ به نموذج المتابعة من الطلب: الاسم والرقم، والرسالة كملاحظة تُعرّف مصدرها */
export function prefillFromWebLead(l: WebLead): LeadPrefill {
  const extras = extraFields(l).map(([k, v]) => `${k}: ${v}`);
  const notes = [`طلب من الموقع${l.formName ? ` (${l.formName})` : ''}`, l.message?.trim() || null, ...extras].filter(Boolean).join('\n');
  return { customerName: l.customerName, phone: l.phoneE164 ?? l.phoneRaw ?? '', notes };
}

/**
 * `mine`: الأدمن يرى هنا ما أخذه **لنفسه** ليردّ عليه (قرار المالك 23/09/2026) — بنفس البطاقة
 * وبنفس «سجّل متابعة»، فلا مسارٌ ثانٍ للردّ على العميل.
 */
export function WebLeadsCard({ onUse, mine = false, meId = null }:
  { onUse: (p: LeadPrefill) => void; mine?: boolean; /** للأدمن: «أرد أنا» يأخذ الطلب له */ meId?: string | null }) {
  const [data, setData] = useState<WebLeadsResponse | null>(null);
  /** للأدمن: كل طلبات الموقع — سجلٌّ بحالتها ومن ردّ (قرار المالك 25/09/2026) */
  const [all, setAll] = useState<WebLead[] | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [m, a] = await Promise.all([
        get<WebLeadsResponse>(mine ? '/web-leads?mine=true' : '/web-leads'),
        mine ? get<WebLeadsResponse>('/web-leads') : Promise.resolve(null),
      ]);
      setData(m); setAll(a?.items ?? null); setErr(null);
    }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'تعذّر تحميل طلبات الموقع'); }
  }, [mine]);
  // مع النبضة (27/09/2026): تُعاد القراءة حين يتغيّر مفتاح طلبات الموقع لا كل دقيقة؛ وبلا نبضة كما كان
  const pulse = usePulse();
  const hasPulse = pulse !== null && pulse.healthy;   // نبضةٌ فاشلة = بلا نبضة: يعود السؤال القديم (مراجعة 27/09/2026)
  useEffect(() => {
    void load();
    if (hasPulse) return;
    const id = setInterval(() => { void load(); }, 60_000);
    return () => clearInterval(id);
  }, [load, hasPulse]);
  const pulseKey = pulse?.key.webLeads ?? '';
  useEffect(() => { if (pulseKey) void load(); }, [pulseKey, load]);

  if (data === null && !err) return <Card title="🌐 طلبات من موقع iConTravel"><Spinner /></Card>;

  const items = data?.items ?? [];
  const open = items.filter(l => !l.followUpId);
  /**
   * **«أرد أنا»** (قرار المالك 25/09/2026: «انا عاوز ارد انا كمان لو حبيت ارد على حد فيهم واقدر اسجله
   * متابعة عادى زى البياع»): طلبٌ لم يُردّ عليه يأخذه لنفسه أوّلًا — فمتابعتُه تُقفله باسمه لا باسم من
   * كان عنده — ثم يُملأ نموذج المتابعة كما يفعل البياع. وما ردّ عليه غيرُه تُسجَّل له متابعةٌ أخرى وحسب.
   */
  async function answerMyself(l: WebLead) {
    setBusy(l.id); setErr(null);
    try {
      if (!l.followUpId && l.assignedTo !== meId && meId) await post(`/admin/web-leads/${l.id}/assign`, { userId: meId });
      onUse(prefillFromWebLead(l));
      await load();
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'تعذّر أخذ الطلب'); }
    finally { setBusy(null); }
  }

  /**
   * **الحذف من السجلّ** (قرار المالك 26/09/2026: «عاوز اعرف امسح الطلبات دى لان دى طلبات وهمية … زرار اقدر
   * امسح بيه واحده واحده … علامة الباسكت … و زرار يمسحهم كلهم مره واحده»، و«عاوز امسح برضو الطلبات ال عند
   * البياعين … هما ميعرفوش يمسحوها»): الحذف التامّ نفسه — يختفي من كل قائمة، ومن عند البياع أيضًا.
   * و«امسح الكل» يمسح **ما يراه الآن** بالمعرّفات: طلبٌ وصل بعد فتح الشاشة لا يُمسح معها.
   */
  async function removeOne(l: WebLead) {
    setBusy(l.id); setErr(null);
    try { await post(`/admin/web-leads/${l.id}/delete`, {}); await load(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'تعذّر الحذف'); }
    finally { setBusy(null); }
  }
  async function removeAll(ids: string[]) {
    setBusy('*'); setErr(null);
    try { await post('/admin/web-leads/delete-many', { ids }); await load(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'تعذّر الحذف'); }
    finally { setBusy(null); }
  }

  // السجلّ بلا ما عُرض فوق (المفتوح عندك) — فلا يظهر الطلب مرّتين
  const openIds = new Set(open.map(l => l.id));
  const log = (all ?? []).filter(l => !openIds.has(l.id));
  const unanswered = (all ?? []).filter(l => !l.followUpId).length;

  return (
    <Card title="🌐 طلبات من موقع iConTravel" action={
      <div className="flex items-center gap-2">
        {open.length > 0 && <Badge tone="bg-rose-100 text-rose-800">{open.length} لسه ما اتكلمش</Badge>}
        {mine && unanswered > open.length && (
          <Badge tone="bg-amber-100 text-amber-900">{unanswered} في الشركة لسه محدش ردّ</Badge>
        )}
        <button type="button" className="btn-ghost px-3 py-1.5 text-sm" onClick={() => void load()}>تحديث</button>
      </div>
    }>
      {err && <Alert>{err}</Alert>}
      <div className="mb-3 rounded-lg bg-sky-50 p-3 text-sm leading-6 text-sky-900">
        <b>دول عملاء ملوا نموذج على موقع الشركة</b> وجه عليك الدور. كلّمهم في أقرب وقت، واضغط «سجّل متابعة» — الطلب يتقفل بالمتابعة ويختفي من هنا.
      </div>

      {open.length === 0 ? (
        <Empty>مفيش طلبات مفتوحة من الموقع دلوقتي — أول ما يوصلك طلب هيرنّ الجرس ويظهر هنا.</Empty>
      ) : (
        <ul className="divide-y divide-slate-100">
          {open.map(l => <LeadRow key={l.id} lead={l} onUse={onUse} canDelete={mine} onDeleted={load} />)}
        </ul>
      )}

      {mine && all !== null && (
        <LeadsLog items={showAll ? log : log.slice(0, 15)} total={log.length}
          showAll={showAll} onToggle={() => setShowAll(v => !v)}
          busy={busy} onAnswer={meId ? l => void answerMyself(l) : undefined}
          onDelete={l => void removeOne(l)} onDeleteAll={() => void removeAll((all ?? []).map(l => l.id))}
          allCount={(all ?? []).length} />
      )}

    </Card>
  );
}

/** «رد بعد قد إيه» — من وصول الطلب إلى تسجيل متابعته */
export function responseTime(fromIso: string, toIso: string): string {
  const min = Math.max(0, Math.round((Date.parse(toIso) - Date.parse(fromIso)) / 60_000));
  if (min < 60) return `بعد ${min} دقيقة`;
  const h = Math.round(min / 60);
  if (h < 24) return `بعد ${h} ساعة`;
  return `بعد ${Math.round(h / 24)} يوم`;
}

/**
 * **سجلّ طلبات الموقع عند المالك** (قراره 25/09/2026: «عاوز لما يجى طلبات من الموقع تتسجل عندى هنا كمان
 * و عاوز اعرف اترد عليه ولا متردش عليه ومين ال رد عليها من الشباب»). كل طلب: وصل إمتى، وحالته — اترد
 * (ومين ردّ وبعد قد إيه)، أو لسه عند مين، أو في الطابور. قراءةٌ وحدها: النقل والحذف في «متابعات الفريق».
 */
function LeadsLog({ items, total, showAll, onToggle, busy, onAnswer, onDelete, onDeleteAll, allCount }:
  { items: WebLead[]; total: number; showAll: boolean; onToggle: () => void;
    busy: string | null; onAnswer?: (l: WebLead) => void;
    onDelete: (l: WebLead) => void; onDeleteAll: () => void; allCount: number }) {
  /** الحذف بضغطتين: الأولى تسأل والثانية تنفّذ — بلا نافذة منبثقة، كبقيّة الحذف في البرنامج */
  const [confirming, setConfirming] = useState<string | null>(null);
  const ask = (key: string, act: () => void) => { if (confirming === key) { setConfirming(null); act(); } else setConfirming(key); };
  return (
    <section className="mt-4 border-t border-slate-100 pt-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-bold text-ink-800">سجل كل طلبات الموقع</h3>
        {allCount > 0 && (
          <div className="flex items-center gap-1.5">
            <button type="button" disabled={busy === '*'} onClick={() => ask('*', onDeleteAll)}
              className={`btn-ghost px-3 py-1 text-xs ${confirming === '*' ? 'border-rose-300 bg-rose-50 text-rose-700' : 'text-rose-700'}`}>
              {confirming === '*' ? `تأكيد: امسح الـ${allCount} طلب نهائي` : 'امسح الكل'}
            </button>
            {confirming === '*' && <button type="button" className="btn-ghost px-3 py-1 text-xs" onClick={() => setConfirming(null)}>إلغاء</button>}
          </div>
        )}
      </div>
      {total === 0 ? <Empty>لسه ما وصلش طلبات من الموقع.</Empty> : (
        <ul className="divide-y divide-slate-100 text-sm">
          {items.map(l => (
            <li key={l.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
              <b className="text-ink-900">{l.customerName}</b>
              <span dir="ltr" className="font-mono text-ink-600">{l.phoneE164 ?? l.phoneRaw ?? '—'}</span>
              {l.formName && <span className="text-xs text-ink-500">{l.formName}</span>}
              <span className="text-xs tabular-nums text-ink-500">وصل {cairoDateAr(l.receivedAt)}</span>
              <span className="ms-auto flex flex-wrap items-center gap-2">
                <LeadStatus lead={l} />
                {onAnswer && (
                  <button type="button" disabled={busy === l.id} onClick={() => onAnswer(l)}
                    className={`${l.followUpId ? 'btn-ghost' : 'btn-primary'} px-3 py-1 text-xs`}>
                    {l.followUpId ? 'سجّل متابعة' : 'أرد أنا'}
                  </button>
                )}
                <button type="button" disabled={busy === l.id} onClick={() => ask(l.id, () => onDelete(l))}
                  aria-label={confirming === l.id ? `تأكيد حذف ${l.customerName}` : `حذف ${l.customerName}`}
                  title={confirming === l.id ? 'اضغط تاني للتأكيد' : 'حذف'}
                  className={`rounded-lg px-2 py-1 text-xs ${confirming === l.id ? 'bg-rose-600 text-white' : 'text-rose-700 hover:bg-rose-50'}`}>
                  {confirming === l.id ? 'تأكيد؟' : <TrashIcon />}
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      {total > 15 && (
        <button type="button" className="btn-ghost mt-2 px-3 py-1.5 text-sm" onClick={onToggle}>
          {showAll ? 'اعرض الأحدث بس' : `اعرض الكل (${total})`}
        </button>
      )}
    </section>
  );
}

function LeadStatus({ lead: l }: { lead: WebLead }) {
  if (l.followUpId) {
    const who = l.answeredName ?? l.assignedName ?? '—';
    return (
      <Badge tone="bg-emerald-100 text-emerald-900">
        ✅ اترد — {who}{l.answeredAt ? ` · ${responseTime(l.receivedAt, l.answeredAt)}` : ''}
      </Badge>
    );
  }
  if (l.assignedTo) return <Badge tone="bg-rose-100 text-rose-800">⏳ لسه ما اتردّش — عند {l.assignedName ?? '—'}</Badge>;
  return <Badge tone="bg-amber-100 text-amber-900">في الطابور — مستني حد يبقى أونلاين</Badge>;
}

/**
 * `canDelete`: للأدمن في «طلباتي» وحده (قرار المالك 23/09/2026: «لو عاوز احذفها انا فقط احذفها عادى») —
 * البياع لا يحذف طلبًا وصله، يسجّل متابعته. والحذف **بتأكيدٍ بنقرة** كما في بطاقة الأدمن (لا كتابة)،
 * والخادم يرفضه لغير الأدمن على أي حال.
 */
function LeadRow({ lead: l, onUse, canDelete = false, onDeleted }:
  { lead: WebLead; onUse: (p: LeadPrefill) => void; canDelete?: boolean; onDeleted?: () => Promise<void> | void }) {
  const extras = extraFields(l);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function remove() {
    if (!confirming) { setConfirming(true); return; }
    setBusy(true); setErr(null);
    try { await post(`/admin/web-leads/${l.id}/delete`, {}); await onDeleted?.(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'تعذّر الحذف'); setBusy(false); }
  }
  return (
    <li className="py-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="bg-brand-600 text-white">من الموقع</Badge>
            {l.formName && <Badge tone="bg-slate-100 text-ink-600">{l.formName}</Badge>}
            {l.assignmentReason === 'ADMIN' && <Badge tone="bg-amber-100 text-amber-900">نقله لك الأدمن</Badge>}
            <b className="text-ink-900">{l.customerName}</b>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            {l.phoneE164
              ? <span dir="ltr" className="font-mono text-ink-700"><PhoneActions phone={l.phoneE164} /></span>
              : <span className="text-amber-800">الرقم كما كتبه: <span dir="ltr" className="font-mono">{l.phoneRaw || '—'}</span></span>}
            {l.email && <span dir="ltr" className="text-ink-600">{l.email}</span>}
          </div>
          {l.message && <p className="mt-1.5 whitespace-pre-line text-sm leading-6 text-ink-800">{l.message}</p>}
          {extras.length > 0 && (
            <dl className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-ink-600">
              {extras.map(([k, v]) => <div key={k}><dt className="inline font-medium">{k}:</dt> <dd className="inline">{v}</dd></div>)}
            </dl>
          )}
          <div className="mt-1 text-xs tabular-nums text-ink-500">وصل {cairoDateAr(l.receivedAt)}</div>
        </div>
        <div className="flex flex-col items-stretch gap-2">
          <button type="button" className="btn-primary px-4 py-2 text-sm" onClick={() => onUse(prefillFromWebLead(l))}>
            سجّل متابعة
          </button>
          {canDelete && (
            <div className="flex items-center gap-1.5">
              <button type="button" disabled={busy} onClick={() => void remove()}
                className={`btn-ghost flex-1 px-3 py-1.5 text-sm ${confirming ? 'border-rose-300 bg-rose-50 text-rose-700' : 'text-rose-700'}`}>
                {confirming ? 'تأكيد الحذف' : 'حذف'}
              </button>
              {confirming && !busy && (
                <button type="button" autoFocus className="btn-ghost px-3 py-1.5 text-sm" onClick={() => setConfirming(false)}>إلغاء</button>
              )}
            </div>
          )}
        </div>
      </div>
      {err && <div className="mt-2"><Alert>{err}</Alert></div>}
    </li>
  );
}
